import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import '../css/ConfirmAdminChangesDialog.css';

interface ChangesSummary {
  lineItemId: string;
  productName: string;
  productSku: string;
  changes: {
    field: string;
    fieldLabel: string;
    oldValue: string;
    newValue: string;
  }[];
}

interface ConfirmAdminChangesDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: () => void;
  changesSummary: ChangesSummary[];
  saving: boolean;
}

export const ConfirmAdminChangesDialog: React.FC<ConfirmAdminChangesDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  changesSummary,
  saving
}) => {
  const getChangeIcon = (fieldLabel: string) => {
    switch (fieldLabel.toLowerCase()) {
      case 'fulfillment status':
      case 'status':
        return 'pi pi-refresh';
      case 'fulfillment location':
      case 'location':
        return 'pi pi-map-marker';
      case 'decline reason':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-pencil';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'assigned':
        return 'pi pi-check-circle';
      case 'unassigned':
        return 'pi pi-clock';
      case 'fulfilled':
        return 'pi pi-verified';
      case 'declined':
      case 'store_declined_order':
        return 'pi pi-times-circle';
      default:
        return 'pi pi-circle';
    }
  };

  const formatStatusText = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getFormattedValue = (value: string, fieldLabel: string, type: 'before' | 'after') => {
    if (!value || value === 'null' || value === 'undefined') {
      return (
        <div className="confirm-changes-dialog__empty-value">
          <i className="pi pi-minus"></i>
          <span>Not set</span>
        </div>
      );
    }
    
    // Add status icons for fulfillment status
    if (fieldLabel.toLowerCase().includes('status')) {
      const statusIcon = getStatusIcon(value);
      return (
        <div className="confirm-changes-dialog__status-value">
          <i className={statusIcon}></i>
          <span>{formatStatusText(value)}</span>
        </div>
      );
    }
    
    // Add location icons for fulfillment location
    if (fieldLabel.toLowerCase().includes('location')) {
      return (
        <div className="confirm-changes-dialog__location-value">
          <i className="pi pi-building"></i>
          <span>{value}</span>
        </div>
      );
    }
    
    return <span>{value}</span>;
  };

  return (
    <Dialog
      header="Confirm Changes"
      visible={visible}
      onHide={onHide}
      style={{ width: '70vw', maxWidth: '800px' }}
      modal
      className="confirm-changes-dialog"
      contentClassName="confirm-changes-dialog__content"
      headerClassName="confirm-changes-dialog__header"
      footer={
        <div className="confirm-changes-dialog__footer">
          <Button 
            label="Cancel" 
            icon="pi pi-times" 
            onClick={onHide}
            className="confirm-changes-dialog__button--cancel"
            disabled={saving}
            severity="danger"
          />
          <Button 
            label={saving ? "Saving..." : "Yes, Save Changes"}
            icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
            onClick={onConfirm}
            className="confirm-changes-dialog__button--save"
            disabled={saving}
            loading={saving}
            severity="success"
          />
        </div>
      }
    >
      <div className="confirm-changes-dialog__body">
        <Message 
          severity="warn" 
          text={`You are about to update ${changesSummary.length} line item(s). Please review the changes below:`}
          className="confirm-changes-dialog__warning-message"
        />
        
        <div className="confirm-changes-dialog__changes-summary">
          {changesSummary.map((item, index) => (
            <div key={item.lineItemId} className="confirm-changes-dialog__change-item">
              <Card className="confirm-changes-dialog__change-card">
                {/* Product Header */}
                <div className="confirm-changes-dialog__product-header">
                  <div className="confirm-changes-dialog__product-info">
                    <i className="pi pi-box confirm-changes-dialog__product-icon"></i>
                    <div className="confirm-changes-dialog__product-details">
                      <div className="confirm-changes-dialog__product-name">{item.productName}</div>
                      <div className="confirm-changes-dialog__product-sku">SKU: {item.productSku}</div>
                    </div>
                  </div>
                </div>
                
                <Divider className="confirm-changes-dialog__divider" />
                
                {/* Changes Grid */}
                <div className="confirm-changes-dialog__changes-grid">
                  {item.changes.map((change, changeIndex) => (
                    <div key={changeIndex} className={`confirm-changes-dialog__change-section confirm-changes-dialog__change-section--${change.fieldLabel.toLowerCase().replace(' ', '-')}`}>
                      {/* Change Type Header */}
                      <div className="confirm-changes-dialog__change-header">
                        <i className={`${getChangeIcon(change.fieldLabel)} confirm-changes-dialog__change-icon`}></i>
                        <span className="confirm-changes-dialog__change-type">{change.fieldLabel.toUpperCase()}</span>
                      </div>
                      
                      {/* Before/After Cards */}
                      <div className="confirm-changes-dialog__before-after">
                        <div className="confirm-changes-dialog__before-card">
                          <div className="confirm-changes-dialog__card-label">BEFORE</div>
                          <div className="confirm-changes-dialog__card-value">
                            {getFormattedValue(change.oldValue, change.fieldLabel, 'before')}
                          </div>
                        </div>
                        
                        <div className="confirm-changes-dialog__arrow-container">
                          <i className="pi pi-arrow-right confirm-changes-dialog__flow-arrow"></i>
                        </div>
                        
                        <div className="confirm-changes-dialog__after-card">
                          <div className="confirm-changes-dialog__card-label">AFTER</div>
                          <div className="confirm-changes-dialog__card-value">
                            {getFormattedValue(change.newValue, change.fieldLabel, 'after')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              
              {index < changesSummary.length - 1 && <Divider className="confirm-changes-dialog__item-divider" />}
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}; 